import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Paper,
  Typography, Box, Chip, Select, MenuItem, FormControl, InputLabel,
  Grid, CircularProgress, Alert, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon, Widgets as WidgetsIcon, Search as SearchIcon,
  CloudUpload as CloudUploadIcon, Build as BuildIcon, Description as DescriptionIcon,
  PersonOutline as PersonOutlineIcon, Schedule as ScheduleIcon, AutoMode as AutoModeIcon,
  CloudDownload as CloudDownloadIcon, Repeat as RepeatIcon, HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import WidgetSnapshotDialog from './WidgetSnapshotDialog';
import ActivityAssistantDialog from './ActivityAssistantDialog';
import ActionBrowserEvidenceDialog from './ActionBrowserEvidenceDialog';
import { normalizeActionLogTimestamp } from '../Utils/timestampUtils';
import CloseButton from './Common/CloseButton';
import AppTooltip from './Common/AppTooltip';
import { useWebSocket } from './WebSocketContext';
import { apiRequest } from '../Utils/apiClient';
import { fetchActionLogs, getActionHistoryScope } from '../Utils/actionsApi';
import {
  ACTION_OUTCOMES, getLogFileName, getActionLogOutcome, getActionLogLabel, getActionLogIdentity,
  getActionLogOrigin, getActionLogOriginInfo, getActionLogExplanation, getActionLogSkipConditionExplanation
} from '../Utils/actionLogPresentation';
import { getActionLogFormSettings } from '../Utils/actionLogFormPresentation';

const primaryColor = '#5757b0';

const EMPTY_FILTERS = Object.freeze({});
const ActionLogOutcomeChip = ({ outcome, count, translate: t, sx }) => {
  const [open, setOpen] = useState(false);
  const [fallback, color, explanation] = ACTION_OUTCOMES[outcome];
  const name = t(`actionsLogs.activity.outcomes.${outcome}`, fallback);
  const label = count === undefined ? name : `${count} · ${name}`;
  return <AppTooltip title={t(`actionsLogs.activity.outcomeHelp.${outcome}`, explanation)} describeChild
    open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)} enterTouchDelay={0} leaveTouchDelay={2500}>
    <Chip component="span" role="group" aria-label={label} tabIndex={0} size="small" variant="outlined" color={color} label={label}
      onKeyDown={event => { if (event.key === 'Escape' && open) { event.stopPropagation(); setOpen(false); } }}
      sx={{ ...sx, cursor: 'help', height: 'auto', minHeight: 24, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 },
        '&:focus-visible': { outline: `2px solid ${primaryColor}`, outlineOffset: 2 } }} />
  </AppTooltip>;
};

const ORIGIN_ICONS = {
  manual: PersonOutlineIcon, scheduled: ScheduleIcon, automatic: AutoModeIcon,
  import: CloudDownloadIcon, repeated: RepeatIcon, retry: RepeatIcon, unknown: HelpOutlineIcon
};

const ActionLogOriginIndicator = ({ context, translate }) => {
  const { kind, label } = getActionLogOriginInfo(context, translate);
  const Icon = ORIGIN_ICONS[kind];
  return (
    <Tooltip title={label} arrow enterTouchDelay={0} leaveTouchDelay={2000}>
      <Box component="span" role="img" aria-label={label} tabIndex={0}
        sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, borderRadius: 1, color: 'text.secondary', cursor: 'help',
          '&:focus-visible': { outline: `2px solid ${primaryColor}`, outlineOffset: 2 } }}>
        <Icon sx={{ fontSize: 20 }} />
      </Box>
    </Tooltip>
  );
};

const ActionLogName = ({ log, label, description, icon, catalogEntry, translate: t }) => {
  const [open, setOpen] = useState(false);
  const { sections, partial, hasRecordedValues } = useMemo(() => getActionLogFormSettings(log, t, catalogEntry), [log, t, catalogEntry]);
  const details = <Box sx={{ '& .MuiTypography-root': { color: 'inherit' } }}>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
    <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>{description || t('actionsLogs.activity.noDescription', 'No description available.')}</Typography>
    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>{t('actionsLogs.activity.recordedSettings', 'Settings when this action ran')}</Typography>
    {sections.length ? <>
      <Typography variant="caption">{t('actionsLogs.activity.recordedSettingsHelp', 'Saved choices. Values filled in automatically may differ.')}</Typography>
      {sections.map(section => <Box key={section.title} sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.25)' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{section.title}</Typography>
        <Box component="dl" sx={{ m: 0, mt: 1 }}>
          {section.fields.map(({ label: field, value }, index) => <Box key={index} sx={{ mb: 1 }}>
            <Box component="dt" sx={{ opacity: 0.8 }}>{field}</Box>
            <Box component="dd" sx={{ m: 0, mt: 0.25, whiteSpace: 'pre-wrap', fontWeight: 500 }}>{value}</Box>
          </Box>)}
        </Box>
      </Box>)}
      {partial && <Typography variant="caption">{t('actionsLogs.activity.moreRecordedSettings', 'More saved details are available under Technical details.')}</Typography>}
    </> : hasRecordedValues ? <Typography variant="body2" sx={{ mt: 0.75 }}>{t('actionsLogs.activity.settingsInDetails', 'The saved settings are available under Technical details.')}</Typography>
    : <Typography variant="body2" sx={{ mt: 0.75 }}>{t('actionsLogs.activity.noRecordedSettings', 'No readable settings were recorded in this entry.')}</Typography>}
  </Box>;
  return <AppTooltip title={details} describeChild open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)} enterTouchDelay={0} leaveTouchDelay={3000}
    componentsProps={{ tooltip: { sx: { maxWidth: 420, maxHeight: 'min(60vh, 420px)', overflowY: 'auto', overflowWrap: 'anywhere' } } }}>
    <Box component="span" role="group" aria-label={label} tabIndex={0} onKeyDown={event => {
      if (event.key === 'Escape' && open) { event.stopPropagation(); setOpen(false); }
    }} sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 1, minWidth: 0, cursor: 'help', borderRadius: 0.5,
      '&:focus-visible': { outline: `2px solid ${primaryColor}`, outlineOffset: 3 } }}>
      {icon}<Typography component="span" variant="body2" sx={{ fontWeight: 600, textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}>{label}</Typography>
    </Box>
  </AppTooltip>;
};

export const formatActionsLogsDialogTitle = (value, maxLength = 100) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return 'Event Logs';
  }
  if (rawValue.length <= maxLength) {
    return rawValue;
  }
  return `...${rawValue.slice(-maxLength)}`;
};

const AI_ERROR_CODES = new Set([
  'azure_public_access_disabled', 'azure_permission_denied', 'ai_permission_denied',
  'ai_authentication', 'ai_input_too_large', 'ai_model_unavailable', 'ai_connection',
  'ai_invalid_response', 'ai_service_unavailable', 'ai_rate_limited', 'ai_timeout',
  'insufficient_credits', 'ai_content_filter', 'ai_processing_error'
]);

const UNKNOWN_AI_ERROR = 'The AI could not process this document. The exact cause is not available in this message. Try again; if it persists, ask an administrator to check the processing logs.';

export const getActionLogDetailsSummary = (log = {}, translate) => {
  const status = String(log.status || '').toLowerCase();
  const isSkipped = status === 'skipped' || status === 'cached';
  const errorDetails = String(log.error_details || '').trim();
  const successDetails = String(log.success_details || '').trim();

  if (errorDetails) {
    let readableDetails = errorDetails;
    if (log.action_type === 'file_process') {
      let safeError;
      try {
        const parsed = JSON.parse(errorDetails);
        if (AI_ERROR_CODES.has(parsed?.code) && typeof parsed.message === 'string') {
          safeError = parsed;
        }
      } catch {
        // Older logs stored a status sentence rather than the safe JSON contract.
      }
      const legacyStatus = errorDetails.match(/^File processing ended with status (ERROR_AI|ERROR_AI_THROTTLE|ERROR_TIMEOUT|ERROR_CONTENT_FILTER|3|6|7|11)$/);
      if (!safeError && legacyStatus) {
        const legacyCodes = {
          ERROR_AI_THROTTLE: 'ai_rate_limited', 6: 'ai_rate_limited',
          ERROR_TIMEOUT: 'ai_timeout', 11: 'ai_timeout',
          ERROR_CONTENT_FILTER: 'ai_content_filter', 7: 'ai_content_filter'
        };
        safeError = { code: legacyCodes[legacyStatus[1]] || 'ai_processing_error', message: UNKNOWN_AI_ERROR };
      }
      if (safeError) {
        readableDetails = translate
          ? translate(`actionsLogs.processingErrors.${safeError.code}`, safeError.message)
          : safeError.message;
      }
    }
    return {
      kind: isSkipped ? 'skipped' : 'error',
      details: readableDetails
    };
  }

  if (successDetails) {
    return {
      kind: 'success',
      details: successDetails
    };
  }

  return {
    kind: 'none',
    details: ''
  };
};

export const getActionLogDetailsPreview = (log = {}, maxLength = 140, translate) => {
  const { kind, details } = getActionLogDetailsSummary(log, translate);
  if (kind === 'none') {
    return {
      kind,
      details,
      preview: ''
    };
  }

  const firstLine = details.split(/\r?\n/).find((line) => line.trim()) || details;
  const normalizedPreview = firstLine.trim();
  const preview = normalizedPreview.length > maxLength
    ? `${normalizedPreview.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`
    : normalizedPreview;

  return {
    kind,
    details,
    preview
  };
};

export const getWidgetTokenUsageFromLog = (log = {}) => {
  if (!log.widget_snapshot) return null;

  try {
    const snapshot = typeof log.widget_snapshot === 'string'
      ? JSON.parse(log.widget_snapshot)
      : log.widget_snapshot;
    const usage = snapshot?.context?.ai_token_usage;
    if (!usage || typeof usage !== 'object') return null;

    const totalTokens = Number(usage.total_tokens);
    if (!Number.isFinite(totalTokens)) return null;
    return {
      ...usage,
      input_tokens: Number(usage.input_tokens || 0),
      output_tokens: Number(usage.output_tokens || 0),
      total_tokens: totalTokens,
      request_count: Number(usage.request_count || 0),
      generation_id: usage.generation_id || null
    };
  } catch {
    return null;
  }
};

const ActionsLogsDialog = ({
  open, onClose, title = '', caseid = null, timeevent_id = null,
  showFilters = true, maxHeight = '600px', maxLogs = 20,
  extraFilters = EMPTY_FILTERS
}) => {
  const { t, i18n } = useTranslation();
  const { isConnected, userInfo } = useWebSocket();
  const organizationScope = String(userInfo?.organization_id || '');
  const initialFilters = useMemo(() => ({
    caseid: caseid || '', timeevent_id: timeevent_id || '',
    status: '', action_type: '', execution_context: ''
  }), [caseid, timeevent_id]);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [availableCases, setAvailableCases] = useState([]);
  const [projectsError, setProjectsError] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [previousCursors, setPreviousCursors] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [snapshotContext, setSnapshotContext] = useState(null);
  const [browserContext, setBrowserContext] = useState(null);
  const [assistantContext, setAssistantContext] = useState(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [snapshotError, setSnapshotError] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [actionCatalog, setActionCatalog] = useState({ scope: '', entries: new Map() });
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const catalogRequestSequence = useRef(0);
  const requestSequence = useRef(0);
  const connectionRef = useRef(isConnected);
  // Callers often pass inline objects. Keep their values stable across renders.
  const extraFiltersKey = JSON.stringify(extraFilters || EMPTY_FILTERS);
  const appliedExtraFilters = useMemo(() => JSON.parse(extraFiltersKey), [extraFiltersKey]);
  const lockedProject = String(appliedExtraFilters.caseid ?? '').trim() !== '';
  const effectiveFilters = useMemo(() => ({ ...filters, ...appliedExtraFilters }), [filters, appliedExtraFilters]);
  const assistantFilters = getActionHistoryScope(effectiveFilters);
  const assistantScopeKey = JSON.stringify(assistantFilters);
  const { timeevent_id: filteredEventId, action_id: filteredActionId, datasource_id: filteredDatasourceId } = effectiveFilters;
  const connectorHistory = String(filteredDatasourceId ?? '').trim() !== '';
  const showEventColumn = !connectorHistory && !String(filteredEventId ?? '').trim();
  const showActionColumn = !connectorHistory && !String(filteredActionId ?? '').trim();

  useEffect(() => {
    setAssistantContext(null);
    setAssistantOpen(false);
    setBrowserContext(null);
  }, [organizationScope, effectiveFilters.caseid, assistantScopeKey]);

  const askQuestion = (log = null) => {
    setAssistantContext({ caseid: log?.case_id || effectiveFilters.caseid || caseid, log, filters: assistantFilters,
      label: log ? getActionLogLabel(log, t, getActionLogIdentity(log, actionCatalog.entries.get(log.action_type))) : '' });
    setAssistantOpen(true);
  };

  useEffect(() => {
    setDraftFilters(initialFilters);
    setFilters(initialFilters);
  }, [initialFilters]);

  const loadLogs = useCallback(async (targetCursor = null, stack = []) => {
    if (!open) return;
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError(false);
    try {
      const result = await fetchActionLogs({
        ...filters,
        ...appliedExtraFilters,
        limit: (!filters.caseid && !filters.timeevent_id) ? Math.max(maxLogs, 50) : maxLogs,
        pagination_mode: 'cursor', cursor: targetCursor
      });
      if (sequence !== requestSequence.current) return;
      setLogs(result.logs || []);
      setCursor(result.cursor ?? targetCursor);
      setNextCursor(result.next_cursor ?? null);
      setHasMore(Boolean(result.has_more));
      setPreviousCursors(stack);
    } catch {
      if (sequence === requestSequence.current) setError(true);
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [open, filters, appliedExtraFilters, maxLogs]);

  useEffect(() => {
    loadLogs();
    return () => { requestSequence.current += 1; };
  }, [loadLogs, organizationScope]);

  const loadActionCatalog = useCallback(async () => {
    const sequence = ++catalogRequestSequence.current;
    setCatalogLoading(true);
    setCatalogError(false);
    try {
      const plugins = await apiRequest('/api/v1/action-plugins');
      if (!Array.isArray(plugins)) throw new Error('Invalid action catalogue');
      if (sequence !== catalogRequestSequence.current) return;
      const entries = new Map(plugins.filter(plugin => typeof plugin?.type === 'string').map(plugin => [plugin.type, {
        type: plugin.type, name: plugin.name, description: plugin.description, settings: { fields: plugin.settings?.fields }
      }]));
      setActionCatalog({ scope: organizationScope, entries });
    } catch {
      if (sequence === catalogRequestSequence.current) setCatalogError(true);
    } finally {
      if (sequence === catalogRequestSequence.current) setCatalogLoading(false);
    }
  }, [organizationScope]);

  useEffect(() => {
    if (open && !connectorHistory) {
      setActionCatalog({ scope: organizationScope, entries: new Map() });
      loadActionCatalog();
    } else if (connectorHistory) {
      setCatalogLoading(false);
      setCatalogError(false);
    }
    return () => { catalogRequestSequence.current += 1; };
  }, [open, organizationScope, loadActionCatalog, connectorHistory]);

  const getLogIdentity = (log) => getActionLogIdentity(log,
    actionCatalog.scope === organizationScope ? actionCatalog.entries.get(log.action_type) : null);

  const loadProjects = useCallback(async () => {
    setProjectsError(false);
    try {
      const result = await apiRequest('/api/v1/sidebar/projects?page=1&limit=200');
      setAvailableCases(Array.isArray(result?.cases) ? result.cases : []);
    } catch {
      setProjectsError(true);
    }
  }, []);

  useEffect(() => {
    if (open && !connectorHistory && !lockedProject) loadProjects();
  }, [open, loadProjects, connectorHistory, lockedProject]);

  useEffect(() => {
    const wasConnected = connectionRef.current;
    connectionRef.current = isConnected;
    if (!open || !isConnected) setSnapshotContext(null);
    if (open && isConnected && wasConnected === false) loadLogs();
  }, [open, isConnected, loadLogs]);

  const outcomeLabel = (outcome) => t(
    `actionsLogs.activity.outcomes.${outcome}`, ACTION_OUTCOMES[outcome][0]
  );
  const outcomeStyle = (outcome) => ({ color: {
    done: 'success.dark', failed: 'error.dark', skipped: '#8a4b00',
    reused: 'info.dark', started: 'info.dark', running: 'info.dark'
  }[outcome] || 'text.secondary' });
  const formatTimestamp = (timestamp, log) => {
    if (!timestamp) return t('actionsLogs.activity.dateUnknown', 'Date not recorded');
    const normalized = normalizeActionLogTimestamp(timestamp, log);
    const date = new Date(normalized);
    return Number.isNaN(date.getTime())
      ? t('actionsLogs.activity.dateUnknown', 'Date not recorded')
      : date.toLocaleString(i18n?.language, { dateStyle: 'medium', timeStyle: 'short' });
  };
  const formatDuration = (durationMs) => {
    if (durationMs === null || durationMs === undefined) return '—';
    const duration = Number(durationMs);
    if (!Number.isFinite(duration) || duration < 0) return '—';
    return duration < 1000 ? `${Math.round(duration)} ms` : `${(duration / 1000).toLocaleString(i18n?.language, { maximumFractionDigits: 1 })} s`;
  };
  const updateDraft = (key, value) => setDraftFilters(previous => ({ ...previous, [key]: value }));
  const optionsFor = (key) => {
    const values = [...logs.map(log => log[key]), draftFilters[key], filters[key]].filter(Boolean);
    return key === 'status'
      ? [...new Set(['success', 'failed', 'skipped', ...values.map(value => String(value).toLowerCase() === 'error' ? 'failed' : String(value).toLowerCase())])]
      : [...new Set(values)];
  };
  const counts = logs.reduce((result, log) => {
    const outcome = getActionLogOutcome(log);
    result[outcome] = (result[outcome] || 0) + 1;
    return result;
  }, {});
  const openSnapshot = (log, type) => {
    try {
      const raw = type === 'widget' ? log.widget_snapshot : log.timeevent_snapshot;
      const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!data || typeof data !== 'object') throw new Error('Invalid snapshot');
      setSnapshotError(false);
      setSnapshotContext({ log, type, data });
    } catch {
      setSnapshotError(true);
    }
  };
  const renderTechnicalDetails = (log, key) => {
    const tokenUsage = getWidgetTokenUsageFromLog(log);
    const recordedMessage = getActionLogSkipConditionExplanation(log, t)
      || log.error_details || log.success_details;
    return (
      <Box component="details" open={Boolean(expandedLogs[key])}
        sx={{ mt: 1.5, '& summary': { cursor: 'pointer', color: primaryColor, width: 'fit-content' } }}>
        <Typography component="summary" variant="body2" onClick={event => {
          event.preventDefault();
          setExpandedLogs(previous => ({ ...previous, [key]: !previous[key] }));
        }}>
          {t('actionsLogs.activity.technicalDetails', 'Technical details')}
        </Typography>
        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, overflowWrap: 'anywhere' }}>
          {[
            [t('actionsLogs.activity.recordedAction', 'Recorded action'), [log.action_name, log.action_type].filter(Boolean).join(' · ')],
            [t('actionsLogs.activity.recordedStatus', 'Recorded status'), log.status],
            [t('actionsLogs.activity.recordedContext', 'Recorded origin'), log.execution_context],
            [t('actionsLogs.activity.source', 'Full source'), log.file_source],
            [t('actionsLogs.activity.duration', 'Duration (ms)'), log.duration_ms],
            [t('actionsLogs.activity.eventId', 'Event ID'), log.timeevent_id],
            [t('actionsLogs.activity.recordedMessage', 'Recorded message'), recordedMessage]
          ].filter(([, value]) => value !== undefined && value !== null && value !== '').map(([label, value]) => (
            <Box key={label} sx={{ mb: 0.75 }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{String(value)}</Typography>
            </Box>
          ))}
          {tokenUsage && <Typography variant="body2">{t('actionsLogs.aiTokenUsage', 'AI tokens: {{count}}', { count: new Intl.NumberFormat(i18n?.language).format(tokenUsage.total_tokens) })}</Typography>}
          {tokenUsage && <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(tokenUsage, null, 2)}</Typography>}
          {(log.widget_snapshot || log.timeevent_snapshot) && (
            <Button size="small" onClick={() => openSnapshot(log, log.widget_snapshot ? 'widget' : 'timeevent')}>
              {t('actionsLogs.activity.savedVersion', 'View the saved version')}
            </Button>
          )}
        </Box>
      </Box>
    );
  };
  // The event menu historically supplies a full path in its title.
  const friendlyTitle = title.startsWith('Event Logs - ')
    ? getLogFileName(title.slice('Event Logs - '.length))
    : (title === 'Event Logs' ? '' : title);
  const filtered = Object.keys(filters).some(key => String(filters[key] || '') !== String(initialFilters[key] || ''));

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="action-activity-title"
        PaperProps={{ sx: { borderRadius: 3, minHeight: { xs: 0, sm: `min(${maxHeight}, 80vh)` }, m: { xs: 1.5, sm: 4 }, width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' }, maxHeight: { xs: 'calc(100% - 24px)', sm: '90vh' } } }}>
        <DialogTitle component="div" id="action-activity-header" sx={{ display: 'grid', gridTemplateColumns: '32px minmax(0, 1fr) 32px', alignItems: 'center', gap: 2, bgcolor: primaryColor, color: 'white' }}>
          <Box sx={{ gridColumn: 2, minWidth: 0, textAlign: 'center' }}>
            <Typography id="action-activity-title" component="h2" variant="h6" sx={{ m: 0, color: 'inherit' }}>{t('actionsLogs.activity.title', 'Activity history')}</Typography>
            {friendlyTitle && <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{formatActionsLogsDialogTitle(friendlyTitle)}</Typography>}
          </Box>
          <CloseButton onClick={onClose} ariaLabel={t('actionsLogs.activity.close', 'Close history')} sx={{ gridColumn: 3, justifySelf: 'end', color: 'white' }} />
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 2, mb: 2 }}>
            {t('actionsLogs.activity.intro', 'See what was done, what was skipped and what needs attention. Most recent activity appears first.')}
          </Typography>
          {(effectiveFilters.caseid || caseid) && <Button variant="outlined" size="small" sx={{ mb: 2 }} onClick={() => askQuestion()}>
            {t('activityAssistant.historyQuestion', 'Ask about this history')}
          </Button>}
          {showFilters && (
            <Box component="details" sx={{ mb: 2, '& summary': { cursor: 'pointer', width: 'fit-content' } }}>
              <Typography component="summary" variant="body2" sx={{ color: primaryColor, fontWeight: 600 }}>
                {t('actionsLogs.activity.filters', 'Filter history')}{filtered ? ` · ${t('actionsLogs.activity.filtersActive', 'Filters applied')}` : ''}
              </Typography>
              <Box component="form" onSubmit={event => { event.preventDefault(); setFilters({ ...draftFilters }); }} sx={{ pt: 2 }}>
                {projectsError && <Alert severity="warning" action={<Button onClick={loadProjects}>{t('actionsLogs.activity.retry', 'Try again')}</Button>} sx={{ mb: 2 }}>{t('actionsLogs.activity.projectsError', 'The project list could not be loaded. You can still view the current history.')}</Alert>}
                <Grid container spacing={2}>
                  {!connectorHistory && !lockedProject && <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="activity-project-label">{t('actionsLogs.activity.project', 'Project')}</InputLabel>
                      <Select labelId="activity-project-label" label={t('actionsLogs.activity.project', 'Project')} value={draftFilters.caseid}
                        onChange={event => setDraftFilters(previous => ({ ...previous, caseid: event.target.value, timeevent_id: '' }))}>
                        <MenuItem value="">{t('actionsLogs.activity.allProjects', 'All projects')}</MenuItem>
                        {draftFilters.caseid && !availableCases.some(project => String(project.id) === String(draftFilters.caseid)) && <MenuItem value={draftFilters.caseid}>{t('actionsLogs.activity.currentProject', 'Current project')}</MenuItem>}
                        {availableCases.map(project => <MenuItem key={project.id} value={project.id}>{project.title || project.name || t('actionsLogs.activity.untitledProject', 'Untitled project')}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>}
                  {(connectorHistory ? ['status'] : ['status', 'action_type', 'execution_context']).map(key => {
                    const label = { status: t('actionsLogs.activity.result', 'Result'), action_type: t('actionsLogs.activity.action', 'Action'), execution_context: t('actionsLogs.activity.origin', 'Started by') }[key];
                    return <Grid key={key} item xs={12} sm={6}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id={`activity-${key}-label`}>{label}</InputLabel>
                        <Select labelId={`activity-${key}-label`} label={label} value={draftFilters[key]} onChange={event => updateDraft(key, event.target.value)}>
                          <MenuItem value="">{t('actionsLogs.activity.all', 'All')}</MenuItem>
                          {optionsFor(key).map(value => <MenuItem key={value} value={value}>{key === 'status'
                            ? (value === 'success' ? t('actionsLogs.activity.successFilter', 'Completed actions or accepted requests') : outcomeLabel(getActionLogOutcome({ status: value })))
                            : key === 'action_type' ? getActionLogLabel({ action_type: value }, t, getLogIdentity({ action_type: value })) : getActionLogOrigin(value, t)}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>;
                  })}
                </Grid>
                {!connectorHistory && <Box component="details" sx={{ mt: 2, '& summary': { cursor: 'pointer' } }}>
                  <Typography component="summary" variant="body2">{t('actionsLogs.activity.advanced', 'Advanced options')}</Typography>
                  <TextField size="small" sx={{ mt: 2 }} label={t('actionsLogs.activity.eventId', 'Event ID')} value={draftFilters.timeevent_id}
                    onChange={event => updateDraft('timeevent_id', event.target.value)} helperText={t('actionsLogs.activity.eventIdHelp', 'Optional. Leave empty to include all events in the selected project.')} />
                </Box>}
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button type="submit" variant="contained" disabled={loading}>{t('actionsLogs.activity.apply', 'Apply filters')}</Button>
                  <Button disabled={loading} onClick={() => { setDraftFilters(initialFilters); setFilters(initialFilters); }}>{t('actionsLogs.activity.reset', 'Reset filters')}</Button>
                </Box>
              </Box>
            </Box>
          )}
          {catalogError && <Alert severity="warning" sx={{ mb: 2 }} action={<Button onClick={loadActionCatalog}>{t('actionsLogs.activity.retry', 'Try again')}</Button>}>
            {t('actionsLogs.activity.catalogError', 'Some action names and descriptions could not be loaded. Try again to display them.')}
          </Alert>}
          {loading || catalogLoading ? <Box role="status" sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 4, justifyContent: 'center' }}><CircularProgress size={24} /><Typography>{t('actionsLogs.activity.loading', 'Loading history…')}</Typography></Box>
            : error ? <Alert severity="error" action={<Button onClick={() => loadLogs(cursor, previousCursors)}>{t('actionsLogs.activity.retry', 'Try again')}</Button>}>{t('actionsLogs.activity.loadError', 'The history could not be loaded. Check your connection and try again. Your filters have been kept.')}</Alert>
              : logs.length === 0 ? <Box sx={{ py: 5, textAlign: 'center' }}><Typography variant="h6">{t('actionsLogs.activity.empty', 'No activity to show')}</Typography><Typography color="text.secondary">{t('actionsLogs.activity.emptyHelp', 'Actions will appear here after they run. If you applied filters, try resetting them.')}</Typography></Box>
                : <>
                  <Typography variant="caption" color="text.secondary">{t('actionsLogs.activity.onThisPage', 'On this page')}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75, mb: 2 }}>
                    {Object.keys(ACTION_OUTCOMES).filter(outcome => counts[outcome]).map(outcome => <ActionLogOutcomeChip key={outcome} outcome={outcome} count={counts[outcome]} translate={t} sx={outcomeStyle(outcome)} />)}
                  </Box>
                  <TableContainer component={Paper} variant="outlined" tabIndex={0}
                    role="region" aria-label={t('actionsLogs.activity.title', 'Activity history')}
                    sx={{ borderRadius: 2, overflowX: 'auto' }}>
                    <Table size="small" aria-label={t('actionsLogs.activity.title', 'Activity history')}
                      sx={{ tableLayout: 'fixed', minWidth: 660 + (showEventColumn ? 130 : 0) + (showActionColumn ? 250 : 0), '& th, & td': { px: 1.25, py: 1.5, verticalAlign: 'top', overflowWrap: 'anywhere' }, '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 125 }}>{t('actionsLogs.activity.date', 'Date')}</TableCell>
                          {showActionColumn && <TableCell sx={{ width: 250 }}>{t('actionsLogs.activity.action', 'Action')}</TableCell>}
                          <TableCell sx={{ width: 105 }}>{t('actionsLogs.activity.result', 'Result')}</TableCell>
                          <TableCell sx={{ width: 90 }} align="center">{t('actionsLogs.activity.origin', 'Started by')}</TableCell>
                          <TableCell sx={{ width: 65 }} align="right">{t('actionsLogs.activity.elapsedTime', 'Duration')}</TableCell>
                          {showEventColumn && <TableCell sx={{ width: 130 }}>{t('actionsLogs.activity.event', 'Event')}</TableCell>}
                          <TableCell sx={{ width: 275 }}>{t('actionsLogs.details', 'Details')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {logs.map((log, index) => {
                          const outcome = getActionLogOutcome(log);
                          const identity = getLogIdentity(log);
                          const actionLabel = getActionLogLabel(log, t, identity);
                          const { description } = identity;
                          return <TableRow key={log.id ?? index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' }, '&:hover': { bgcolor: 'action.hover' } }}>
                            <TableCell component={showActionColumn ? 'td' : 'th'} scope={showActionColumn ? undefined : 'row'}
                              sx={{ '&.MuiTableCell-root': { bgcolor: 'transparent', fontWeight: 400 } }}><Typography variant="body2">{formatTimestamp(log.timestamp, log)}</Typography></TableCell>
                            {showActionColumn && <TableCell component="th" scope="row" sx={{ '&.MuiTableCell-root': { bgcolor: 'transparent' } }}>
                              <ActionLogName log={log} label={actionLabel} description={description} icon={getActionTypeIcon(log.action_type)}
                                catalogEntry={actionCatalog.scope === organizationScope ? actionCatalog.entries.get(log.action_type) : null} translate={t} />
                            </TableCell>}
                            <TableCell><ActionLogOutcomeChip outcome={outcome} translate={t} sx={outcomeStyle(outcome)} /></TableCell>
                            <TableCell align="center"><ActionLogOriginIndicator context={log.execution_context} translate={t} /></TableCell>
                            <TableCell align="right"><Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{formatDuration(log.duration_ms)}</Typography></TableCell>
                            {showEventColumn && <TableCell><Typography variant="body2">{getLogFileName(log.file_source) || '—'}</Typography></TableCell>}
                            <TableCell>
                              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>{getActionLogExplanation(log, t, getActionLogDetailsSummary(log, t).details)}</Typography>
                              {renderTechnicalDetails(log, log.id ?? index)}
                              {log.id && log.case_id && (log.has_browser_evidence || ['browseweb', 'cmqverifydoctor', 'oiiqverifynurse'].includes(log.action_type)) &&
                                <Button size="small" sx={{ mt: 1 }} onClick={() => setBrowserContext({ log, scope: organizationScope })}>
                                  {t('actionsLogs.browser.open', 'View navigation')}
                                </Button>}
                              {log.id && log.case_id && <Button size="small" sx={{ mt: 1 }} onClick={() => askQuestion(log)}>
                                {t('activityAssistant.title', 'Ask a question')}
                              </Button>}
                            </TableCell>
                          </TableRow>;
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>}
          {snapshotError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSnapshotError(false)}>{t('actionsLogs.activity.snapshotError', 'The saved version could not be opened. Ask an administrator to check this history entry.')}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: '1px solid', borderColor: 'divider', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button disabled={loading || !previousCursors.length} onClick={() => loadLogs(previousCursors[previousCursors.length - 1], previousCursors.slice(0, -1))}>{t('actionsLogs.activity.previous', 'Previous')}</Button>
            <Typography variant="body2">{t('actionsLogs.activity.page', 'Page {{page}}', { page: previousCursors.length + 1 })}</Typography>
            <Button disabled={loading || !hasMore || !nextCursor} onClick={() => loadLogs(nextCursor, [...previousCursors, cursor])}>{t('actionsLogs.activity.next', 'Next')}</Button>
          </Box>
          <Button disabled={loading} startIcon={<RefreshIcon />} onClick={() => loadLogs(cursor, previousCursors)}>{t('actionsLogs.activity.refresh', 'Refresh')}</Button>
        </DialogActions>
      </Dialog>
      <WidgetSnapshotDialog open={Boolean(snapshotContext)} onClose={() => setSnapshotContext(null)} snapshotContext={snapshotContext}
        formatTimestamp={timestamp => formatTimestamp(timestamp, snapshotContext?.log)} />
      {open && browserContext?.scope === organizationScope && <ActionBrowserEvidenceDialog
        key={`${organizationScope}:${browserContext.log.id}`} log={browserContext.log} onClose={() => setBrowserContext(null)} />}
      {assistantContext && <ActivityAssistantDialog open={open && assistantOpen} onClose={() => setAssistantOpen(false)}
        caseid={assistantContext.caseid} log={assistantContext.log} filters={assistantContext.filters} contextLabel={assistantContext.label} organizationScope={organizationScope} />}
    </>
  );
};

const getActionTypeIcon = (actionType) => {
  const type = String(actionType || '').toLowerCase();
  const sx = { fontSize: 18, color: primaryColor, flexShrink: 0, mt: '2px' };
  if (type === 'retry_scheduled') return <RepeatIcon sx={sx} />;
  if (/widget/.test(type)) return <WidgetsIcon sx={sx} />;
  if (/scan|search|analysis|analyze/.test(type)) return <SearchIcon sx={sx} />;
  if (/upload|ingest|import/.test(type)) return <CloudUploadIcon sx={sx} />;
  if (/transform|process|parse|extract/.test(type)) return <BuildIcon sx={sx} />;
  return <DescriptionIcon sx={sx} />;
};

export default ActionsLogsDialog;
